/* =========================================================================
   LAYER'D Toolkit — cloud sync layer
   File: assets/learn-cloud-sync.js  ·  loads AFTER learn-cakefile.js
   (subnav keeps document order with defer)

   Keeps each logged-in member's Toolkit data saved to their ACCOUNT
   (via the /apps/toolkit app proxy -> Cloudflare Worker), so it
   follows the login across devices and into the home-screen app.

   WHAT SYNCS (round 3 — backup buttons retired, sync must cover
   everything the backup file used to carry; course progress added
   2026-08-13 with the Two-Tier Foundations build):
     - layerd-academy-bookings    client files (+ pantry & shopping
                                  ticks, which live on the same store)
     - layerd-academy-my-recipes  the member's own recipes
     - layerd-academy-my-combos   the member's own combos
     - layerd-academy-progress    Academy course progress. Items are
                                  {id:"<course>/<lesson>", at} per
                                  completed lesson plus one
                                  {id:"last:<course>", lesson, at}
                                  continue-pointer — written by
                                  academy-course.js in exactly this
                                  collection shape so un-completing a
                                  lesson tombstones like any delete.

   Design rules:
   - ADDITIVE ONLY. localStorage stays the working copy; every tool
     keeps working exactly as today if this file is deleted, the
     member is logged out, the worker is down, or the app proxy
     doesn't exist yet (fetch 404s -> sync silently disables).
   - NO edits to tool files. We ride the 'layerd:cakefile'
     CustomEvent, cross-tab 'storage' events, a light 10s watch on
     the extra keys (the Recipes page saves without an event), and a
     flush when the page hides.
   - Merge never loses work: per collection, union by item id. An id
     on both sides goes to whichever copy was TOUCHED more recently
     (touch stamps for bookings, item.at when present), tie -> local.
   - DELETIONS travel as tombstones (round 2, Mariya's live find
     2026-07-29). Per-device SEEN map of synced "<key>|<id>" pairs;
     a pair that was seen but is gone was deleted HERE -> receipt
     {pair: deletedAt} ships in the envelope; merge drops an item
     whose receipt outdates its last edit. Fresh devices have an
     empty seen map -> never false-tombstone. Receipts prune at 90d.
   - First login with local data + empty cloud = automatic migration up.
   ========================================================================= */
(function () {
  'use strict';

  var ENDPOINT = '/apps/toolkit/store';
  var COLLECTIONS = [
    { key: 'layerd-academy-bookings', list: 'bookings', touchAware: true },
    { key: 'layerd-academy-my-recipes', list: 'items' },
    { key: 'layerd-academy-my-combos', list: 'combos' },
    { key: 'layerd-academy-progress', list: 'items' }
  ];
  var TOUCH_KEY = 'layerd-cloud-touch';          /* {bookingId: epochMs} */
  var SEEN_KEY = 'layerd-cloud-seen';            /* {"key|id": 1} synced */
  var TOMB_KEY = 'layerd-cloud-tombs';           /* {"key|id": deletedAt} */
  var OWNER_KEY = 'layerd-cloud-owner';          /* {id, email, at} whose data this browser holds */
  var PARK_PREFIX = 'layerd-parked:';            /* + customer id -> parked bundle */
  var EVT = 'layerd:cakefile';
  var LEGACY_EVT = 'layerd:cake-updated';
  var SELF = 'cloud-sync';                       /* our event source tag */
  var PUSH_DEBOUNCE_MS = 4000;
  var WATCH_MS = 10000;                          /* extra-key change poll */
  var TOMB_TTL_MS = 90 * 24 * 60 * 60 * 1000;    /* forget deletes after 90d */
  /* r44 (2026-09-16, Mariya's live incident): the truncated script left sync
     dead for a stretch; the two devices diverged, and on the first pull back
     detectLocalDeletes() read the SEEN map from BEFORE the gap and tombstoned
     files this device simply never received again - untouched quote/inquiry
     files quietly "deleted". A gap in sync is NOT a signal that anything was
     deleted. So: on the FIRST run of a new script version, or after a long
     sync gap, do a REUNION - union both sides, apply NO tombstones, reset the
     delete-tracking. Worst case a genuinely-deleted file reappears once; a
     file is never lost. Bumping SYNC_EPOCH forces one reunion on deploy, which
     also repairs a divergence already in progress. */
  var SYNC_EPOCH = 'v44';
  var EPOCH_KEY = 'layerd-cloud-epoch';          /* last SYNC_EPOCH this device healed at */
  var LASTPULL_KEY = 'layerd-cloud-lastpull';    /* epochMs of the last good pull (persisted) */
  var REUNION_GAP_MS = 7 * 24 * 60 * 60 * 1000;  /* a >7-day sync gap = treat as an outage, reunite */

  var enabled = null;      /* null = unknown, false = off for session */
  var pushTimer = null;
  var lastPushedKeys = null;   /* JSON of doc.keys last known in the cloud */
  var watchSnapshot = null;
  var status = { state: 'init', lastPull: 0, lastPush: 0 };

  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }
  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  function readMap(key) { return parse(lsGet(key)) || {}; }
  function writeMap(key, m) { lsSet(key, JSON.stringify(m)); }

  function stamp(bookingId) {
    if (!bookingId) return;
    var t = readMap(TOUCH_KEY);
    t[bookingId] = Date.now();
    writeMap(TOUCH_KEY, t);
  }
  function absorbNewer(key, cloudMap) {
    var m = readMap(key), changed = false;
    for (var id in cloudMap) {
      if (own(cloudMap, id) && (+cloudMap[id] || 0) > (+m[id] || 0)) { m[id] = +cloudMap[id]; changed = true; }
    }
    if (changed) writeMap(key, m);
    return m;
  }

  function listOf(col, store) {
    return (store && isArr(store[col.list])) ? store[col.list] : null;
  }
  function pairId(col, id) { return col.key + '|' + id; }

  /* pairs this device synced before that are now gone = deleted here */
  function detectLocalDeletes() {
    var seen = readMap(SEEN_KEY);
    var tombs = readMap(TOMB_KEY);
    var changedSeen = false, changedTombs = false;
    COLLECTIONS.forEach(function (col) {
      var current = {};
      (listOf(col, parse(lsGet(col.key))) || []).forEach(function (it) {
        if (it && it.id) current[pairId(col, it.id)] = 1;
      });
      for (var pair in seen) {
        if (!own(seen, pair) || pair.indexOf(col.key + '|') !== 0) continue;
        if (!current[pair]) {
          if (!own(tombs, pair)) { tombs[pair] = Date.now(); changedTombs = true; }
          delete seen[pair]; changedSeen = true;
        }
      }
    });
    if (changedSeen) writeMap(SEEN_KEY, seen);
    if (changedTombs) writeMap(TOMB_KEY, tombs);
  }

  function pruneTombs(tombs) {
    var cutoff = Date.now() - TOMB_TTL_MS, out = {}, id;
    for (id in tombs) { if (own(tombs, id) && (+tombs[id] || 0) > cutoff) out[id] = +tombs[id]; }
    return out;
  }

  function markSeen() {
    var seen = {};
    COLLECTIONS.forEach(function (col) {
      (listOf(col, parse(lsGet(col.key))) || []).forEach(function (it) {
        if (it && it.id) seen[pairId(col, it.id)] = 1;
      });
    });
    writeMap(SEEN_KEY, seen);
  }

  /* how recently was this item meaningfully edited, per side */
  function editedAt(col, it, touch) {
    var stamp = 0;
    if (col.touchAware) {
      stamp = Math.max((it.cake && it.cake.updatedAt) || 0, (touch && touch[it.id]) || 0);
    }
    return Math.max(stamp, +it.at || 0);
  }

  /* ---------- merge one collection: union by id, newer-touched wins,
                tie -> local, tombstone newer than every edit -> gone --- */
  function mergeCollection(col, local, cloud, localTouch, cloudTouch, tombs) {
    var result;
    var cl = listOf(col, cloud), ll = listOf(col, local);
    if (!cl) result = { store: local, changed: false };
    else if (!ll) result = { store: cloud, changed: true };
    else {
      var byId = {}, order = [], changed = false;
      ll.forEach(function (b) { if (b && b.id) { byId[b.id] = b; order.push(b.id); } });
      cl.forEach(function (cb) {
        if (!cb || !cb.id) return;
        var lb = byId[cb.id];
        if (!lb) {                                   /* cloud-only -> adopt */
          byId[cb.id] = cb; order.push(cb.id); changed = true;
        } else if (editedAt(col, cb, cloudTouch) > editedAt(col, lb, localTouch)) {
          byId[cb.id] = cb; changed = true;          /* cloud copy newer */
        }                                            /* tie or older -> local stays */
      });
      var merged = {};
      for (var k in local) { if (own(local, k)) merged[k] = local[k]; }
      merged[col.list] = order.map(function (id) { return byId[id]; });
      result = { store: merged, changed: changed };
    }

    /* deletion receipts beat everything older than them */
    var rl = listOf(col, result.store);
    if (rl && tombs) {
      var kept = rl.filter(function (b) {
        if (!b || !b.id || !own(tombs, pairId(col, b.id))) return true;
        var edited = Math.max(editedAt(col, b, localTouch), editedAt(col, b, cloudTouch));
        return edited > +tombs[pairId(col, b.id)];  /* edited AFTER delete -> lives */
      });
      if (kept.length !== rl.length) {
        result.store[col.list] = kept;
        result.changed = true;
      }
    }
    return result;
  }

  /* ---------- envelope on the wire ------------------------------------- */
  function buildKeys() {
    var keys = {};
    COLLECTIONS.forEach(function (col) {
      var raw = lsGet(col.key);
      if (raw) { var p = parse(raw); if (p) keys[col.key] = p; }
    });
    return keys;
  }

  function announce() {
    try { window.dispatchEvent(new CustomEvent(EVT, { detail: { type: 'store', source: SELF, bookingId: null } })); } catch (e) { }
    try { window.dispatchEvent(new Event(LEGACY_EVT)); } catch (e) { }
  }

  /* ---------- network --------------------------------------------------- */
  function disable(why) {
    enabled = false;
    status.state = 'off:' + why;
    /* PWA Phase 2: the moment sync notices the member is logged out,
       tell the offline service worker so it purges gated page caches —
       faster than waiting for the SW's own next revalidation. Additive:
       no SW registered (or no support) is a silent no-op. */
    if (why === 'logged-out') {
      try {
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'logged-out' });
        }
      } catch (e) { }
    }
  }

  function pull() {
    return fetch(ENDPOINT + '?t=' + Date.now(), { credentials: 'same-origin' })
      .then(function (res) {
        if (res.status === 401 || res.status === 403) { disable('logged-out'); return null; }
        if (res.status === 404) { disable('no-proxy'); return null; }
        if (res.status === 204) { enabled = true; return { empty: true }; }
        if (!res.ok) { disable('error-' + res.status); return null; }
        enabled = true;
        return res.json().catch(function () { return { empty: true }; });
      })
      .catch(function () { disable('offline'); return null; });
  }

  function push(useBeacon) {
    if (enabled === false) return;
    /* r85 (Mariya's wipe scare, 2026-08-23): NO PUSH BEFORE THIS SESSION'S
       FIRST COMPLETED PULL. A freshly reinstalled app starts empty; if the
       user creates one thing before the boot pull lands, this used to
       upload a doc containing only that — and the Worker stores docs
       wholesale, so the account's real copy would be REPLACED by
       almost-nothing. The boot pull sets lastPull on every success path
       (a 204-empty cloud included, so first-ever migration still runs);
       until then, pushes politely re-queue. Offline/logged-out sessions
       disable sync entirely, so this never loops. */
    if (!status.lastPull) {
      if (!useBeacon) schedulePush();
      return;
    }
    detectLocalDeletes();
    var keys = buildKeys();
    var any = false; for (var k in keys) { if (own(keys, k)) { any = true; break; } }
    if (!any) return;                             /* nothing to save */
    var keysJson = JSON.stringify(keys);
    var tombs = pruneTombs(readMap(TOMB_KEY));
    writeMap(TOMB_KEY, tombs);
    if (keysJson === lastPushedKeys) return;      /* cloud already has this */
    var body = JSON.stringify({ v: 1, keys: keys, touch: readMap(TOUCH_KEY), tombs: tombs, pushedAt: Date.now() });
    function sent() {
      lastPushedKeys = keysJson; status.lastPush = Date.now();
      markSeen();
    }
    if (useBeacon && navigator.sendBeacon) {
      /* page is closing — fire-and-forget; worker also accepts POST. A beacon
         also caps at ~64KB, so for a bigger studio it refuses (returns false);
         fall through to a normal fetch then rather than silently skipping. */
      try {
        if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))) { sent(); return; }
      } catch (eB) { /* fall through to fetch */ }
    }
    /* the working save: a plain PUT, NO keepalive. r-2026-09-17 (Mariya:
       "Couldn't save to your account (offline)" on a device that was online):
       fetch with keepalive:true caps the request BODY at 64KB and rejects
       above it — so once a studio's files grew past ~64KB every push threw and
       was mislabelled offline (the GET pull has no such cap, so sync looked
       half-working). Foreground pushes don't need to outlive the page, so the
       flag is gone; the unload flush above uses a beacon for that case. */
    fetch(ENDPOINT, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: body
    }).then(function (res) {
      if (res && res.ok) { sent(); status.state = 'synced'; return; }
      /* round 42: a failed SAVE was invisible - the doc stayed stale on
         every device while the page said synced. Name it. */
      if (res) status.state = 'push-error-' + res.status;
    }).catch(function () { status.state = 'push-error-offline'; /* next change retries */ });
  }

  function schedulePush() {
    if (enabled === false) return;
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(function () { pushTimer = null; push(false); }, PUSH_DEBOUNCE_MS);
  }

  /* ---------- owner guard (2026-08-21, Mariya's shared-computer find) ---
     localStorage belongs to the BROWSER; the cloud doc belongs to the
     ACCOUNT. Before this guard, logging into account B on a browser that
     held account A's data made the next pull MERGE the two studios and
     push A's files into B's cloud ("union never loses work" is right on
     one person's two devices and wrong on two people's one device).

     The stamp: data-lh-cid on the shared subnav (the customer's own id
     on their own page). No stamp (logged out, previews, older theme
     paste) -> the guard is inert, additive like everything here.

     On a switch: the previous owner's collections + sync maps + active
     pointer are PARKED under one namespaced key - never uploaded, never
     merged, never deleted - and any bundle parked earlier for the
     incoming account is restored. Switching back restores everything
     exactly. Runs synchronously at eval: deferred scripts execute in
     document order, so this settles before any tool reads the store. */
  function pageIdentity() {
    var nav = document.querySelector('.learn-subnav[data-lh-cid]');
    if (!nav) return null;
    var id = nav.getAttribute('data-lh-cid');
    return id ? { id: String(id), email: nav.getAttribute('data-lh-email') || '' } : null;
  }
  var OWNED_EXTRA = ['layerd-learn-active-booking'];   /* pointer follows its store */
  function parkBundle(ownerId) {
    var bundle = { v: 1, at: Date.now(), keys: {}, maps: {} };
    COLLECTIONS.forEach(function (col) {
      var raw = lsGet(col.key);
      if (raw) bundle.keys[col.key] = raw;
    });
    [TOUCH_KEY, SEEN_KEY, TOMB_KEY].concat(OWNED_EXTRA).forEach(function (k) {
      var raw = lsGet(k);
      if (raw) bundle.maps[k] = raw;
    });
    if (!lsSet(PARK_PREFIX + ownerId, JSON.stringify(bundle))) return false;
    COLLECTIONS.forEach(function (col) { try { localStorage.removeItem(col.key); } catch (e) { } });
    [TOUCH_KEY, SEEN_KEY, TOMB_KEY].concat(OWNED_EXTRA).forEach(function (k) {
      try { localStorage.removeItem(k); } catch (e) { }
    });
    return true;
  }
  function unparkBundle(ownerId) {
    var bundle = parse(lsGet(PARK_PREFIX + ownerId));
    if (!bundle || !bundle.keys) return false;
    var k;
    for (k in bundle.keys) { if (own(bundle.keys, k)) lsSet(k, bundle.keys[k]); }
    for (k in (bundle.maps || {})) { if (own(bundle.maps, k)) lsSet(k, bundle.maps[k]); }
    try { localStorage.removeItem(PARK_PREFIX + ownerId); } catch (e) { }
    return true;
  }
  function ownerNotice(text) {
    try {
      var note = document.createElement('div');
      note.setAttribute('role', 'status');
      note.style.cssText = 'position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:9999;' +
        'max-width:min(92vw,460px);background:var(--ivory-000,#fff);color:var(--ink-700,#2A2138);' +
        'border:1px solid var(--border-soft,#d9d2c7);border-top:2px solid var(--gold-500,#b99a5f);' +
        'border-radius:8px;padding:14px 40px 14px 16px;font-size:.9rem;line-height:1.5;';
      note.textContent = text;
      var x = document.createElement('button');
      x.type = 'button';
      x.setAttribute('aria-label', 'Dismiss');
      x.style.cssText = 'position:absolute;top:6px;right:8px;border:0;background:none;cursor:pointer;' +
        'font-size:1.1rem;color:inherit;padding:4px;';
      x.textContent = '\u00d7';
      x.addEventListener('click', function () { if (note.parentNode) note.parentNode.removeChild(note); });
      note.appendChild(x);
      document.body.appendChild(note);
    } catch (e) { }
  }
  (function guardOwner() {
    var who = pageIdentity();
    if (!who) return;                              /* logged out / preview / old paste */
    var owner = parse(lsGet(OWNER_KEY));
    if (!owner || !owner.id) {
      /* first stamped visit: whatever is here belongs to this account */
      lsSet(OWNER_KEY, JSON.stringify({ id: who.id, email: who.email, at: Date.now() }));
      return;
    }
    if (String(owner.id) === String(who.id)) {
      if (who.email && who.email !== owner.email) {
        lsSet(OWNER_KEY, JSON.stringify({ id: who.id, email: who.email, at: Date.now() }));
      }
      return;
    }
    /* account switch on this browser */
    if (!parkBundle(owner.id)) return;             /* storage full: leave everything as-is */
    var restored = unparkBundle(who.id);
    lsSet(OWNER_KEY, JSON.stringify({ id: who.id, email: who.email, at: Date.now() }));
    lastPushedKeys = null;
    var msg = restored
      ? 'Welcome back' + (who.email ? ', ' + who.email : '') + ' \u2014 this browser held another account\u2019s Toolkit data, so it was set aside and yours is restored.'
      : 'This browser held another account\u2019s Toolkit data' + (owner.email ? ' (' + owner.email + ')' : '') +
        ' \u2014 it\u2019s been set aside, not deleted. Log back into that account to see it.';
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { ownerNotice(msg); });
    } else ownerNotice(msg);
  })();

  /* r44: is this the first pull on a new script version, or the first after a
     long sync gap? Either way, treat it as an outage recovery - reunite both
     sides, don't trust the delete-tracking. */
  function needsReunion() {
    try {
      if (lsGet(EPOCH_KEY) !== SYNC_EPOCH) return true;      /* new script -> heal once */
      var prev = +lsGet(LASTPULL_KEY) || 0;
      if (prev && (Date.now() - prev) > REUNION_GAP_MS) return true;   /* long gap = outage */
    } catch (e) { }
    return false;
  }
  function markHealthy() {
    lsSet(EPOCH_KEY, SYNC_EPOCH);
    lsSet(LASTPULL_KEY, String(Date.now()));
  }

  /* ---------- boot ------------------------------------------------------ */
  /* returns the pull+merge promise so a manual "Sync now" can wait on it
     (existing callers ignore the return — additive) */
  function init() {
    return pull().then(function (res) {
      if (!res) return;                            /* sync off this session */
      status.lastPull = Date.now();
      if (res.empty) {                             /* nothing in the cloud yet */
        status.state = 'migrating-up';
        markHealthy();
        push(false);                               /* migrate local -> cloud */
        return;
      }
      /* r44: a reunion never invents a delete - skip local-delete detection
         and apply NO tombstones this cycle, so an outage can't drop a file.
         The delete-tracking is reset from the merged union below. */
      var reunion = needsReunion();
      if (!reunion) detectLocalDeletes();          /* receipts before merging */
      var cloudKeys = res.keys || {};
      var cloudTouch = res.touch || {};
      var localTouch = absorbNewer(TOUCH_KEY, cloudTouch);
      /* round-2 compat: docs pushed before collections shipped carry bare
         booking ids as receipt keys — namespace them so they still bite */
      var cloudTombs = {};
      for (var tk in (res.tombs || {})) {
        if (!own(res.tombs, tk)) continue;
        cloudTombs[tk.indexOf('|') === -1 ? COLLECTIONS[0].key + '|' + tk : tk] = res.tombs[tk];
      }
      var tombs;
      if (reunion) {
        tombs = {};                                /* ignore every tombstone this reunion */
        writeMap(TOMB_KEY, {});                     /* and forget the suspect delete history */
      } else {
        tombs = pruneTombs(absorbNewer(TOMB_KEY, cloudTombs));
        writeMap(TOMB_KEY, tombs);
      }

      var anyChanged = false;
      var mergedKeys = {};
      COLLECTIONS.forEach(function (col) {
        var local = parse(lsGet(col.key));
        var cloud = cloudKeys[col.key];
        if (!local && !cloud) return;
        var out = mergeCollection(col, local, cloud, localTouch, cloudTouch, tombs);
        if (out.store) mergedKeys[col.key] = out.store;
        if (out.changed && out.store) {
          lsSet(col.key, JSON.stringify(out.store));
          anyChanged = true;
        }
      });
      if (anyChanged) announce();
      markSeen();
      markHealthy();                               /* r44: this device is now in sync at SYNC_EPOCH */
      status.state = 'synced';
      /* r44: after a reunion, always push - the union may have grown AND the
         cloud may still hold stale tombstones the wholesale write must clear.
         (null the dedupe guard so a keys-identical push still lands.) */
      if (reunion) {
        lastPushedKeys = null;
        schedulePush();
        watchSnapshot = extrasSnapshot();
        return;
      }
      /* if the merged result differs from what the cloud holds, refresh it;
         if identical, remember that so we don't re-push on every page load */
      var cloudCompare = {};
      COLLECTIONS.forEach(function (col) { if (cloudKeys[col.key]) cloudCompare[col.key] = cloudKeys[col.key]; });
      if (JSON.stringify(mergedKeys) === JSON.stringify(cloudCompare)) {
        lastPushedKeys = JSON.stringify(buildKeys());
      } else {
        schedulePush();
      }
      watchSnapshot = extrasSnapshot();
    });
  }

  /* every real change (any tool, this tab) -> stamp + queue a push */
  window.addEventListener(EVT, function (e) {
    var d = (e && e.detail) || {};
    if (d.source === SELF) return;                 /* our own merge write */
    stamp(d.bookingId);
    schedulePush();
  });
  /* cross-tab changes also land in the cloud */
  window.addEventListener('storage', function (e) {
    if (!e || !e.key) return;
    var watched = COLLECTIONS.some(function (col) { return col.key === e.key; });
    if (watched) schedulePush();
  });
  /* the Recipes page saves without an event — watch the extra keys */
  function extrasSnapshot() {
    return COLLECTIONS.map(function (col) { return col.touchAware ? '' : (lsGet(col.key) || ''); }).join(' ');
  }
  setInterval(function () {
    if (enabled === false) return;
    var snap = extrasSnapshot();
    /* only schedule when no push is already pending — push() reads the
       keys at fire time, so a queued push carries this change anyway,
       and resetting the debounce would postpone saves indefinitely on a
       page that keeps changing */
    if (watchSnapshot !== null && snap !== watchSnapshot && !pushTimer) schedulePush();
    watchSnapshot = snap;
  }, WATCH_MS);
  /* leaving the page: flush anything unsent (covers event-less saves) */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
      push(true);                                  /* dedupe guard no-ops if clean */
    }
  });

  window.LAYERD = window.LAYERD || {};
  window.LAYERD.cloudSync = {
    status: function () { return { enabled: enabled, state: status.state, lastPull: status.lastPull, lastPush: status.lastPush }; },
    pushNow: function () { push(false); },
    pullNow: init,
    /* a manual, awaitable full sync (settings' "Sync now"): pull + merge the
       cloud in, then force a push of anything local. Resolves with the status
       once the pull side has settled, so the UI can show the outcome. The push
       is fire-and-forget (its own fetch) - lastPush/state land a beat later. */
    syncNow: function () {
      var done;
      try { done = init(); } catch (e) { done = null; }
      if (!done || !done.then) { try { done = Promise.resolve(); } catch (e2) { done = null; } }
      var finish = function () { try { push(false); } catch (e3) { } return window.LAYERD.cloudSync.status(); };
      return (done && done.then) ? done.then(finish) : finish();
    }
  };

  /* round 40 (Mariya: "created on desktop, not populating on mobile"):
     the installed app never RELOADS - pages stay warm for days - so the
     load-time pull above never ran again and another device's saves sat
     in the cloud unseen. Pull whenever the page returns to the
     foreground; the min-interval keeps quick tab-flips cheap. init() is
     the pull+merge path (already exposed as pullNow) - the listeners
     above are module-level, so re-calling it never double-wires. */
  var lastPullAt = Date.now();
  function pullOnReturn() {
    if (enabled === false) return;
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastPullAt < 15000) return;
    lastPullAt = Date.now();
    init();
  }
  document.addEventListener('visibilitychange', pullOnReturn);
  window.addEventListener('focus', pullOnReturn);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
/* LDSYNC-EOF v44 - if this line is missing from the SERVED file, the upload was truncated and the whole script is dead. */
